import { DynamicWaterwayHub } from './DynamicWaterwayHub';
import { DynamicMapStoreBase } from '../DynamicMapStoreBase';
import { DynamicWaterwayProfileStore } from './DynamicWaterwayProfileStore';
import { DynamicWaterwayTableStore } from './DynamicWaterwayTableStore';
import { DynamicProfileLayerType, ProfileKeyCode } from '../DynamicProfileEnums';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import { esriProjection, FeatureLayer, GroupLayer } from 'cd-eft/utils/esriTypes';
import { convertLinearUnit } from 'cd-eft/utils/structureLayerUtils';
import { Renderers } from 'cd-eft/components/SurveyEngineering/Maps/SETMap/SETMapRenderers';
import stores from "cd-eft/models/Stores";
import { computed } from 'mobx';
import {reachHighlightSymbol, trackerSymbol, pointLabelFields2, pointLabelClass3} from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';

const { waterwayDesignStore } = stores.AppStore;

class DynamicWaterwayMapStore extends DynamicMapStoreBase {
  protected hub: DynamicWaterwayHub;

  protected _alignmentGraphic: Graphic | undefined = void 0;
  // protected _alignmentLine: Polyline | undefined  = void 0;
  protected _lookupDistance: number[] = [];
  protected _lookupXY: number[][] = [];
  protected _trackerGraphic: Graphic | undefined = void 0;
  protected _statinoGraphic: Graphic | undefined = void 0;
  protected _reachStationLayer: FeatureLayer | undefined = void 0;
  protected _reachStationLabelLayer: FeatureLayer | undefined = void 0;
  protected _reachStationIndex: number[] = [];

  protected viewPointerMoveHandle;

  /**
   *
   */
  constructor(hub: DynamicWaterwayHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.WCL, ProfileKeyCode.WLB, ProfileKeyCode.WRB, ProfileKeyCode.WLTB];
  }

  /** abstract method implementation */
  moreSketchVmSetup() { }
  SketchPolyline() { }
  toggleAlignmentSnapping() { }
  SketchPoint() { }
  ReshapePolyline() { }
  LoadEditToMap(data: any, keyCode: ProfileKeyCode) { }
  drawCurve(curveData: any) { }
  removeMapLayer(layer: DynamicProfileLayerType) { }
  configSketchVM() { }
  updateAlignmentOnMap(data: any, keyCode: ProfileKeyCode) { }
  checkToConvertAlignment(index) { }
  redrawAlignmentProfile() { } // Only used for alignment
  generateAlignmentLabelsLayer() { }
  handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    return this.emptyPromiseVoid;
  }

  @computed get stationHitTestOptions(): __esri.MapViewHitTestOptions | undefined {
    if (this._reachStationLayer) {
      const options: __esri.MapViewHitTestOptions = {};
      options.include = this._reachStationLayer;
      return options;
    }
    return undefined;
  }

  async setAlignment(data: any) {
    this.clearAlignment();

    if (!this.dataSpatialReference?.equals(this.mapSpatialReference!)) {
      if (!esriProjection.isLoaded()) {
        await esriProjection.load();
      }
    }

    this._lookupDistance = data.map(point => { return point.distance; });
    const points = data.map(point => {
      return [
        point.inputPoint.x,
        point.inputPoint.y,
        convertLinearUnit(point.inputPoint.z, 'intl-feet', 'meters')
      ];
    });
    const dataLine = new Polyline({
      spatialReference: this.dataSpatialReference,
      hasZ: true,
      paths: [points]
    });
    const line = (
      this.dataSpatialReference?.equals(this.mapSpatialReference!) ?
      dataLine :
      esriProjection.project(dataLine, this.mapSpatialReference!) as Polyline
    );
    this._lookupXY = line.paths[0].map(point => { return [point[0], point[1]]; });
    // this._alignmentGraphic = new Graphic({ geometry: line, symbol: alignmentSymbol });
    // this.sketchLayer.add(this._alignmentGraphic);

    // Add handler to the mouse move event
    this.viewPointerMoveHandle?.remove();
    this.viewPointerMoveHandle = this.mapView?.on('pointer-move', async (e: __esri.ViewPointerMoveEvent) => {
      if (this.stationHitTestOptions) {
        const result = await this.mapView?.hitTest(e, this.stationHitTestOptions);
        if (result?.results[0]) {
          const i: number | undefined = result?.results[0].graphic.attributes['ObjectID'];
          if ((i !== undefined) && (i > -1) && (i < this._reachStationIndex.length)) {
            const index = this._reachStationIndex[i];
            if ((index > -1) && (index < waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.length)) {
              if (this.hub.ProfileStore) {
                (this.hub.ProfileStore as DynamicWaterwayProfileStore).trackStation(waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[index]);
              }
              if (this.hub.TableStore) {
                (this.hub.TableStore as DynamicWaterwayTableStore).highlightRows(index);
              }
              this.trackStation(index);
            }
          }
        } else {
          if (this.hub.ProfileStore) {
            (this.hub.ProfileStore as DynamicWaterwayProfileStore).unTrackUnselectedProfilePoints();
          }
          if (this.hub.TableStore) {
            (this.hub.TableStore as DynamicWaterwayTableStore).clearHighlights();
          }
          this.untrackAllStations();
        }
      }
    });
  }

  clearAlignment() {
    if (this._alignmentGraphic) {
      this.sketchLayer.graphics.remove(this._alignmentGraphic);
      this._alignmentGraphic = undefined;
    }
    this._lookupDistance = [];
    this._lookupXY = [];
    this.clearTracker();
  }

  trackStation(index: number) {
    this.untrackAllStations();
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      const station = waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[index];
      if (!(station.station)) return;
      const l = Number(station.station.replace('+', ''));
      const coordXY = this.interpolateCoords(l, this._lookupDistance, this._lookupXY);
      const z = convertLinearUnit(Number(station.channelZ), 'intl-feet', 'meters');
  
      const point = new Point({
        spatialReference: this.mapSpatialReference,
        hasZ: true,
        x: coordXY[0],
        y: coordXY[1],
        z: z
      });
  
      this._statinoGraphic = new Graphic({
        geometry: point,
        attributes: { index: index },
        symbol: reachHighlightSymbol
      });
      this.sketchLayer.add(this._statinoGraphic);
    }
  }

  untrackStation(index: number) {
    if (this._statinoGraphic && this._statinoGraphic.attributes['index'] === index) {
      this.sketchLayer.graphics.remove(this._statinoGraphic);
      this._statinoGraphic = undefined;
    }
  }

  untrackAllStations() {
    if (this._statinoGraphic) {
      this.sketchLayer.graphics.remove(this._statinoGraphic);
      this._statinoGraphic = undefined;
    }
  }

  setTracker(d: number, z: number) {
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      const coordXY = this.interpolateCoords(d, this._lookupDistance, this._lookupXY);
      const point = new Point({
        spatialReference: this.mapSpatialReference,
        hasZ: true,
        x: coordXY[0],
        y: coordXY[1],
        z: convertLinearUnit(z, 'intl-feet', 'meters')
      })
  
      if (this._trackerGraphic) {
        this._trackerGraphic.geometry = point;
      } else {
        this._trackerGraphic = new Graphic({ geometry: point, symbol: trackerSymbol });
        this.sketchLayer.add(this._trackerGraphic);
      }
    }
  }
  
  clearTracker() {
    if (this._trackerGraphic) {
      this.sketchLayer.graphics.remove(this._trackerGraphic);
      this._trackerGraphic = undefined;
    }
  }

  // protected interpolateCoords(distance: number): number[] {
  //   let iLow = 0;
  //   let iHigh = this._lookupDistance.length - 1;
  //   if (distance < this._lookupDistance[iLow]) {
  //     iHigh = 2;
  //   } else if (distance === this._lookupDistance[iLow]) {
  //     return [this._lookupXY[iLow][0], this._lookupXY[iLow][1]];
  //   } else if (distance === this._lookupDistance[iHigh]) {
  //     return [this._lookupXY[iHigh][0], this._lookupXY[iHigh][1]];
  //   } else if (distance > this._lookupDistance[iHigh]) {
  //     iLow = iHigh - 1;
  //   } else {
  //     let iMid: number;
  //     for(;;) {
  //       if (iHigh - iLow < 2) {
  //         break;
  //       }
  //       iMid = Math.ceil((iHigh + iLow) / 2);
  //       if (distance < this._lookupDistance[iMid]) {
  //         iHigh = iMid;
  //       } else if (distance > this._lookupDistance[iMid]) {
  //         iLow = iMid;
  //       } else {
  //         return [this._lookupXY[iHigh][0], this._lookupXY[iHigh][1]];
  //       }
  //     }
  //   }

  //   return this.interpolateXY(iLow, iHigh, distance);
  // }

  // protected interpolateXY(iLow: number, iHigh: number, distance: number): number[] {
  //   const factor = (this._lookupDistance[iHigh] - distance) / (this._lookupDistance[iHigh] - this._lookupDistance[iLow]);
  //   return [
  //     this._lookupXY[iHigh][0] - (this._lookupXY[iHigh][0] - this._lookupXY[iLow][0]) * factor,
  //     this._lookupXY[iHigh][1] - (this._lookupXY[iHigh][1] - this._lookupXY[iLow][1]) * factor
  //   ];
  // }

  handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    return this.emptyPromiseVoid;
  }
  handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
    return this.emptyPromiseVoid;
  }

  async addStations() {
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      this.clearReachStations();
      const pointSource = [] as Graphic[];

      const stationLabels: any = [];
      this._reachStationIndex = [];
      if (waterwayDesignStore.waterwayDesignObject.waterwayDesignStations && waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.length > 0) {
        let index = 0;
        waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.forEach((station) => {
          if (station.reachId.startsWith('R-')) {
            stationLabels.push({ 
              x: Number(station.station.replace('+', '')),
              y: Number(station.channelZ),
              label: `(${station.station}, ${station.channelZ}, ${station.cutFill})` });
              this._reachStationIndex.push(index);
          }
          index++;
        })
      }

      stationLabels.map((station, idx) => {
        const coordXY = this.interpolateCoords(station.x, this._lookupDistance, this._lookupXY);
        const point = new Graphic({
          geometry: new Point({
            spatialReference: this.mapSpatialReference,
            hasZ: true,
            x: coordXY[0],
            y: coordXY[1],
            z: convertLinearUnit(station.y, 'intl-feet', 'meters')
          }),
          attributes: {
            ObjectID: idx,
            StationInfo: station.label
          },
        });
        pointSource.push(point);
      });

      this._reachStationLayer = new FeatureLayer({
        source: pointSource,
        fields: pointLabelFields2,
        renderer: Renderers.reachStationPointRenderer as any,
        id: 'reach_stations_layer',
        title: 'Reach Stations',
        objectIdField: 'ObjectID'
      });

      this._reachStationLabelLayer = new FeatureLayer({
        source: pointSource,
        fields: pointLabelFields2,
        renderer: Renderers.alignmentStationLabelRenderer as any,
        id: 'reach_station_labels_layer',
        title: 'Reach Station Labels',
        objectIdField: 'ObjectID',
        labelingInfo: [pointLabelClass3]
      });

      if(this.mapView && this.mapView.map) {
        const groupLayer = this.mapView.map.findLayerById(`alignment${waterwayDesignStore.alignmentId}`) as GroupLayer;
        if (groupLayer) {
          groupLayer.add(this._reachStationLayer);
          groupLayer.add(this._reachStationLabelLayer);
          // this._reachStationLayer = undefined;
          this._reachStationLabelLayer = undefined;
        }
      }
    }
  }

  clearReachStations() {
    this._reachStationLayer = undefined;
    this._reachStationLabelLayer = undefined;
    this._reachStationIndex = [];
    if (this.mapView && this.mapView.map) {
      const groupLayer = this.mapView.map.findLayerById(`alignment${waterwayDesignStore.alignmentId}`) as GroupLayer;
      if (groupLayer) {
        const reachStationLayer = groupLayer.findLayerById('reach_stations_layer') as FeatureLayer;
        if (reachStationLayer) {
          const removedLayer = groupLayer.remove(reachStationLayer);
          if (removedLayer) removedLayer.destroy();
        }
        const reachStationLabelLayer = groupLayer.findLayerById('reach_station_labels_layer') as FeatureLayer;
        if (reachStationLabelLayer) {
          const removedLayer = groupLayer.remove(reachStationLabelLayer);
          if (removedLayer) removedLayer.destroy();
        }
      }
    }
  }

  Dispose() {
    this.viewPointerMoveHandle?.remove();
    this.untrackAllStations();
    this.clearAlignment();
    this.clearReachStations();
    super.Dispose();
  }
}

export { DynamicWaterwayMapStore };
